import { PaymentMethod } from './PaymentMethods';

export enum ArcaIssuerTaxProfile {
  MONOTRIBUTO = 'MONOTRIBUTO',
  RESPONSABLE_INSCRIPTO = 'RESPONSABLE_INSCRIPTO',
  CONSUMIDOR_FINAL = 'CONSUMIDOR_FINAL',
}

export enum ArcaInvoiceConcept {
  PRODUCTS = 1,
  SERVICES = 2,
  PRODUCTS_AND_SERVICES = 3,
}

export enum ArcaInvoiceLineItemType {
  PRODUCT = 'product',
  SERVICE = 'service',
}

export enum ArcaDocumentType {
  CUIT = 80,
  CUIL = 86,
  DNI = 96,
  CONSUMIDOR_FINAL = 99,
}

export enum ArcaReceiverVatConditionId {
  RESPONSABLE_INSCRIPTO = 1,
  SUJETO_EXENTO = 4,
  CONSUMIDOR_FINAL = 5,
  MONOTRIBUTO = 6,
}

export enum ArcaPersonType {
  FISICA = 'FISICA',
  JURIDICA = 'JURIDICA',
}

export type ArcaInvoiceSourceType = 'order' | 'sale';

export interface ArcaInvoiceFormData {
  fiscalData: {
    pointOfSale: number | null;
    voucherType: number | null;
    concept: ArcaInvoiceConcept | null;
    issueDate: Date | null;
    serviceDateFrom: Date | null;
    serviceDateTo: Date | null;
    paymentDueDate: Date | null;
    paymentMethod: PaymentMethod | null;
  };
  receiver: {
    documentType: ArcaDocumentType | null;
    documentNumber: string | null;
    legalName: string | null;
    personType: ArcaPersonType | string | null;
    fiscalAddress: string | null;
    receiverVatCondition: ArcaReceiverVatConditionId | number | null;
  };
}

export interface ArcaInvoiceFormCatalogResponse {
  issuer: ArcaInvoiceIssuerData;
  catalogs: ArcaInvoiceCatalogs;
}

export interface ArcaInvoiceIssuerData {
  cuit: string;
  legalName: string;
  personType?: string | null;
  taxStatus?: string | null;
  taxProfile?: ArcaIssuerTaxProfile | null;
  fiscalAddress?: ArcaFiscalAddress | null;
  certificateExpiresAt?: string | null;
}

export interface ArcaFiscalAddress {
  address?: string;
  city?: string;
  province?: string;
  zipCode?: string;
}

export interface ArcaInvoiceCatalogs {
  salesPoints: ArcaCatalogResult<'ptoVenta', ArcaSalesPoint>;
  voucherTypes: ArcaCatalogResult<'cbteTipo', ArcaVoucherType>;
  conceptTypes: ArcaCatalogResult<'conceptoTipo', ArcaCatalogItem>;
  documentTypes: ArcaCatalogResult<'docTipo', ArcaCatalogItem>;
  aliquotTypes: ArcaCatalogResult<'ivaTipo', ArcaCatalogItem>;
  currencyTypes: ArcaCatalogResult<'moneda', ArcaCurrencyType>;
  taxTypes: ArcaCatalogResult<'tributoTipo', ArcaCatalogItem>;
  receiverVatConditions: ArcaCatalogResult<
    'condicionIvaReceptor',
    ArcaReceiverVatCondition
  >;
}

export interface ArcaCatalogResult<TKey extends string, TValue> {
  resultGet?: Partial<Record<TKey, TValue[]>>;
  errors?: {
    err?: ArcaCatalogError[];
  };
}

export interface ArcaCatalogError {
  code: number;
  msg: string;
}

export interface ArcaCatalogItem {
  id: number;
  desc: string;
  fchDesde?: string;
  fchHasta?: string;
}

export interface ArcaSalesPoint {
  nro: number;
  emisionTipo?: string;
  bloqueado?: string;
  fchBaja?: string | null;
}

export interface ArcaVoucherType extends ArcaCatalogItem {}

export interface ArcaCurrencyType {
  id: string;
  desc: string;
  fchDesde?: string;
  fchHasta?: string;
}

export interface ArcaReceiverVatCondition {
  id: number;
  desc: string;
  cmp_Clase: string;
}

export interface ArcaInvoiceLineItem {
  code: string | null;
  type: ArcaInvoiceLineItemType;
  description: string;
  quantity: number;
  unitOfMeasure: string;
  unitOfMeasureCode?: string | number;
  unitPrice: number;
  discountPercentage: number;
  discountAmount: number;
  subtotal: number;
}

export interface ArcaInvoiceSourceCustomer {
  name: string | null;
  documentType?: ArcaDocumentType | null;
  documentNumber?: string | null;
  fiscalAddress?: string | null;
}

export interface ArcaInvoiceSourceDraftResponse {
  source: {
    type: ArcaInvoiceSourceType;
    id: string;
    displayName: string;
    createdAt?: string | Date;
    customerName?: string | null;
    customer?: ArcaInvoiceSourceCustomer | null;
  };
  paymentMethod: PaymentMethod;
  suggestedConcept: ArcaInvoiceConcept;
  items: ArcaInvoiceLineItem[];
  totals: {
    subtotal: number;
    discountAmount: number;
    total: number;
  };
  dates?: {
    issueDate?: string | Date | null;
    serviceDateFrom?: string | Date | null;
    serviceDateTo?: string | Date | null;
    paymentDueDate?: string | Date | null;
  };
}

export interface ArcaInvoiceSourceOption {
  sourceType: ArcaInvoiceSourceType;
  sourceId: string;
  label: string;
  customerName: string | null;
  total: number;
  createdAt: string | Date;
}

export interface ArcaInvoiceValidationPayload {
  fiscalData: ArcaInvoiceFormData['fiscalData'];
  receiver: ArcaInvoiceFormData['receiver'];
  source: ArcaInvoiceSourceDraftResponse['source'];
  items: ArcaInvoiceLineItem[];
  totals: ArcaInvoiceSourceDraftResponse['totals'];
}

export interface ArcaInvoiceValidationResponse {
  ok: boolean;
  normalizedPayload: ArcaInvoiceValidationPayload;
  warnings?: string[];
}
